import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {AdminApi} from "../../../api/api";
import {TemplateListOutput} from "../../../api/values/database-list-output";
import {Stores} from "../../../stores";
import {Permission} from "../../../stores/authentication-store";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {PageRouter} from "../../../utils/route-util";
import {ColumnDef} from "../../atoms/tables/table";
import {deleteDialogStore} from "../../organisms/delete-dialog/delete-dialog-store";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;

export class DatabaseTemplateListPageStore {
    /** Observables */
    @observable public mediaId = "";
    @observable public templateList = new TemplateListOutput();
    @observable public searchQuery?: string;
    @observable public page = DEFAULT_PAGE_NUMBER;
    @observable public pageSize = DEFAULT_PAGE_SIZE;
    @observable public hasTitleParam = false;

    private adminApi: AdminApi;
    private stores: Stores;
    private pageRouter: PageRouter;

    /** Constructor */
    constructor(options: PageStoreConstructorOptions) {
        makeObservable(this);
        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;
    }

    /** テーブルヘッダー */
    private static readonly DEFAULT_COLUMN_DEFS: ColumnDef[] = [
        {label: "テンプレート名", required: false, width: 356},
        {label: "説明", required: false, width: 421},
        {label: "作成者", required: false, width: 140},
        {label: "作成日", required: false, width: 160},
        {label: "", required: false, width: 94},
    ];

    public get DEFAULT_COLUMN_DEFS() {
        return DatabaseTemplateListPageStore.DEFAULT_COLUMN_DEFS;
    }

    /** ページャー */
    @computed public get startIndex() {
        return (this.page - 1) * this.pageSize + 1;
    }

    @computed public get endIndex() {
        return Math.min(this.page * this.pageSize, this.templateList.totalCount);
    }

    /** 初期ロード */
    @action.bound
    public async initialize({query}: {query: {mediaId: string; page?: string; title?: string}}) {
        this.stores.authenticationStore.checkAnyPermissionExists(
            Permission.MANAGE_SYSTEM,
            Permission.MANAGE_MEDIA,
        );
        this.mediaId = query.mediaId ?? "";
        this.page = query.page ? Math.max(1, Number(query.page)) : 1;
        this.searchQuery = query.title;
        this.hasTitleParam = !!query.title;
        await this.loadData(this.page);
    }

    /** テンプレート一覧取得 */
    @action.bound
    public async loadData(targetPage: number = 1) {
        this.page = targetPage;
        try {
            const res = await this.adminApi.getTemplates({
                title: this.searchQuery,
                page: this.page,
                mediaId: this.mediaId,
                pageSize: DEFAULT_PAGE_SIZE,
            });
            runInAction(() => {
                this.templateList = res;
            });
        } catch (error) {
            console.error("Error loading templates:", error);
        }
    }

    /** URL 更新 */
    private updateUrl(includePage: boolean = false) {
        const base = `/admin/${this.mediaId}/database-templates`;
        const params: string[] = [];
        if (this.searchQuery) params.push(`title=${encodeURIComponent(this.searchQuery)}`);
        if (includePage) params.push(`page=${this.page}`);
        const url = params.length ? `${base}?${params.join("&")}` : base;
        this.pageRouter.pushRoute(url);
    }

    /** アクション */
    @action.bound
    public setSearch(query: string) {
        this.searchQuery = query;
    }

    @action.bound
    public async searchAndLoad() {
        this.page = 1;
        this.hasTitleParam = !!this.searchQuery;
        this.updateUrl();
    }

    @action.bound
    public async clearSearch() {
        this.searchQuery = undefined;
        this.hasTitleParam = false;
        this.page = 1;
        this.updateUrl();
    }

    @action.bound
    public async changePage(page: number) {
        this.page = page;
        this.updateUrl(true);
    }

    // ページ遷移
    @action.bound
    public toCreatePage() {
        this.pageRouter.pushRoute(`/admin/${this.mediaId}/database-templates/create`);
    }

    @action.bound
    public toDetailPage(id: string) {
        this.pageRouter.pushRoute(`/admin/${this.mediaId}/database-templates/detail/${id}`);
    }

    @action.bound
    public toEditPage(id: string) {
        this.pageRouter.pushRoute(`/admin/${this.mediaId}/database-templates/detail/${id}/edit`);
    }

    @action.bound
    public async confirmDeleteTemplate(id: string, title: string) {
        deleteDialogStore.open(
            "テンプレート削除",
            `<strong>「${title}」</strong>を削除します。よろしいですか？<br/>この操作は取り消せません。`,
            async () => {
                await this._deleteTemplate(id);
                await this.loadData(this.page);
                this.updateUrl(true);
            },
        );
    }

    /** 削除API call */
    @action.bound
    private async _deleteTemplate(id: string) {
        try {
            await this.adminApi.deleteTemplate({mediaId: this.mediaId}, {templateId: id});
        } catch (error) {
            console.error("Error deleting template:", error);
        }
    }
}
